import { Editor, Point, Range } from 'slate';
import { OverlappingTag } from '../types';

export interface TagSegment {
  anchor: Point;
  focus: Point;
}

// Pure boundary-segment computation shared by every tag-insert path (#5).
// Given a selection and the existing tags it overlaps, returns the sub-ranges
// that should each become their own tag wrapper — splitting the selection at the
// points where it enters ('entry') or exits ('exit') an existing tag, so the
// new tag never re-wraps text that already belongs to another tag.
export const computeOverlapSegments = (editor: Editor, selection: Range, overlapping: OverlappingTag[]): TagSegment[] => {
  const [selStart, selEnd] = Range.edges(selection);

  const boundaries: { inside: Point; outside: Point; type: 'exit' | 'entry' }[] = [];

  for (const overlap of overlapping) {
    const { path: tagPath, overlapType } = overlap;
    const tagRange = Editor.range(editor, tagPath);
    const [tagStart, tagEnd] = Range.edges(tagRange);

    if (overlapType === 'end') {
      const pointAfterTag = Editor.after(editor, tagPath);
      if (pointAfterTag && Point.compare(pointAfterTag, selEnd) <= 0) {
        boundaries.push({ inside: tagEnd, outside: pointAfterTag, type: 'exit' });
      }
    } else if (overlapType === 'start') {
      const pointBeforeTag = Editor.before(editor, tagPath);
      if (pointBeforeTag && Point.compare(selStart, pointBeforeTag) <= 0) {
        boundaries.push({ outside: pointBeforeTag, inside: tagStart, type: 'entry' });
      }
    }
  }

  boundaries.sort((a, b) => Point.compare(a.outside, b.outside));

  const segments: TagSegment[] = [];
  let currentStart = selStart;

  for (const boundary of boundaries) {
    if (boundary.type === 'exit') {
      if (Point.compare(currentStart, boundary.inside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.inside });
      }
      currentStart = boundary.outside;
    } else if (boundary.type === 'entry') {
      if (Point.compare(currentStart, boundary.outside) < 0) {
        segments.push({ anchor: currentStart, focus: boundary.outside });
      }
      currentStart = boundary.inside;
    }
  }

  if (Point.compare(currentStart, selEnd) < 0) {
    segments.push({ anchor: currentStart, focus: selEnd });
  }

  return segments.filter((seg) => Point.compare(seg.anchor, seg.focus) < 0);
};
